import Button from '@/components/Buttons/Button';
import { CancelModal } from '@/components/Modals/CancelModal';
import SimpleNotificationModal from '@/components/Modals/SimpleNotificationModal';
import { toast } from '@/components/toast';
import { churnkeyAppId, churnkeyEnvironment, pricingPageUrl } from '@/environment';
import { useGetSupportUrl } from '@/hooks/useGetSupportUrl';
import { useUnlimitedFirstMonthCreditsEnabled } from '@/hooks/useUnlimitedFirstMonthCreditsEnabled';
import CrownBlack from '@/images/CrownBlack.svg';
import { Credit, getChurnKey, getNextInvoice, getSubscription } from '@/services/api';
import { DidUser, isStripePayingUser, useAuth } from '@/services/auth';
import { useFlag, useFlagExperiment } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { creditsToVideoMinutes, formatCredits, useCreditQuery } from '@/state/credits/credits';
import { useGetStripeCustomerPortalUrl } from '@/state/products/Products';
import { PaymentProvider } from '@/state/user-settings/types';
import { useGetOrganization } from '@/state/video-library/videoLibrary';
import { HttpStatus } from '@d-id/http-status';
import { PlanGroup, productToProductName, ProductType } from '@d-id/types';
import styled from '@emotion/styled';
import { Grid, Skeleton, Typography } from '@mui/material';
import { format } from 'date-fns';
import mixpanel from 'mixpanel-browser';
import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import Card from './Card';

const PlanBadge = styled(Typography, { shouldForwardProp: propName => propName !== 'canceled' })<{ canceled: boolean }>`
    color: ${({ theme, canceled }) => (canceled ? theme.colors.black[600] : theme.colors.primary.main)};
    background-color: ${({ theme, canceled }) => (canceled ? theme.colors.black[100] : theme.colors.primary.bg)};
    width: fit-content;
    padding: 6px 8px 6px 8px;
    border-radius: 4px;
    text-transform: uppercase;
    margin-bottom: 0.75rem;
    gap: 10px;
`;

const CreditsContainer = styled.div`
    display: flex;
    flex-direction: initial;
    gap: 4px;
    align-items: center;
    justify-content: center;
`;

const SeparateLine = styled.div`
    height: 1px;
    border-radius: 8px;
    background-color: ${({ theme }) => theme.colors.black[100]};
`;

const CreditsSkeleton = styled(Skeleton)<{ width: string }>`
    height: 2.4rem;
    width: ${({ width }) => width};
`;

const UsedCredits = styled.span<{ isExceeding: boolean }>`
    color: ${({ isExceeding, theme }) => (isExceeding ? theme.colors.error.main : theme.colors.black.main)};
`;

const CancelMessageContainer = styled.div`
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    gap: 4px;
    display: flex;
    flex-direction: column;
`;

const getPlanText = ({
    isOrg,
    isOrgNameFetched,
    orgName,
    user,
    cancelAt,
    credits,
    creditsToMinutesEnabled,
    unlimitedFirstMonthCreditsEnabled,
}: {
    isOrg: boolean;
    isOrgNameFetched: boolean;
    orgName: string;
    user: DidUser | null | undefined;
    cancelAt?: number | null | undefined;
    credits?: Credit;
    creditsToMinutesEnabled: boolean;
    unlimitedFirstMonthCreditsEnabled: boolean;
}) => {
    if (isOrg) {
        return isOrgNameFetched ? orgName : 'Loading...';
    }
    const userPlan = user?.planGroup ?? user?.productName;
    const creditsText = unlimitedFirstMonthCreditsEnabled
        ? 'Unlimited'
        : creditsToMinutesEnabled
          ? creditsToVideoMinutes(credits!.total)
          : formatCredits(credits!.total);
    const planText =
        userPlan &&
        `${productToProductName[userPlan]}${[PlanGroup.GUEST, ProductType.GUEST].includes(userPlan) ? '' : ' plan'}` + ` (${creditsText})`;

    if (cancelAt) {
        if (cancelAt * 1000 < Date.now()) return 'Canceled';
        return `${planText} - Ends on ${format(new Date(cancelAt * 1000), 'dd/MM/yyyy')}`;
    }
    return planText;
};

const FieldsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 1.4rem;
`;

const ActionButton = styled(Button, { shouldForwardProp: p => !(p === 'hidden') })<{
    disabled: boolean;
}>`
    padding: 10px 16px 10px 16px;
    font-size: 1.6rem;
    border-radius: 8px;
    line-height: 1;
    width: 100%;
    height: 40px;
    min-height: 40px;
    display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
    gap: 6px;
`;

const ActionButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 14px;
    justify-content: flex-end;
`;

const BillingTitle = styled(Typography)`
    font-style: normal;
    font-weight: 700;
    margin-top: 1.6rem;
    margin-bottom: 1.6rem;
`;

const BillingCycleTypography = styled(Typography)`
    margin-bottom: 16px;
    margin-top: 8px;
    color: ${({ theme }) => theme.colors.secondary[600]};
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.32px;
`;

const SupportLink = styled(Link)`
    font-weight: 600;
`;

const CancelDate = styled.span`
    font-weight: 600;
`;

interface BillingProps {
    flex: number;
}

export enum cancelationProcessFlag {
    control = 1,
    churnkey = 2,
    roll_over_credits = 3,
}

export const GET_SUBSCRIPTION_KEY = 'get-last-subscription';

export default function Billing({ flex }: BillingProps) {
    const { isAnonymous, user } = useAuth();
    const { isFetching: fetchingCustomerPortalUrl, refetch: refetchCustomerPortalUrl } = useGetStripeCustomerPortalUrl();
    const [isCancelingSubscription, setIsCancelingSubscription] = useState(false);
    const cancelationFlag = useFlagExperiment('cancelation_process_groups', cancelationProcessFlag.control);
    const { data: org, isFetched: isOrgNameFetched } = useGetOrganization();
    const { data: credits, isFetched: isCreditsFetched } = useCreditQuery();
    const isOrg = !!user?.org_id;
    const [isCancelationDialogOpen, setIsCancelationDialogOpen] = useState(false);
    const [openMobileModal, setOpenMobileModal] = useState(false);
    const isSuggestDiscount =
        user?.planGroup === PlanGroup.LITE || user?.planGroup === PlanGroup.PRO || user?.planGroup === PlanGroup.ADVANCED;
    const { data: invoices, isFetching: isFetchingInvoices } = isStripePayingUser(user)
        ? useQuery('get-next-invoice', getNextInvoice)
        : { data: undefined, isFetching: false };
    const { data: subscriptionData } = user?.stripeCustomerId ? useQuery(GET_SUBSCRIPTION_KEY, getSubscription) : { data: undefined };
    const cancelAt = subscriptionData?.subscription?.cancel_at;
    const supportUrl = useGetSupportUrl();
    const queryClient = useQueryClient();
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);
    const { enabled: unlimitedFirstMonthCreditsEnabled, isFetched: unlimitedFirstMonthCreditsIsFetched } =
        useUnlimitedFirstMonthCreditsEnabled(user);

    const onBillingClick = async () => {
        mixpanel?.track('billing', { event: 'click' });
        if (user?.paymentProvider === PaymentProvider.AppStore) {
            mixpanel?.track('goto-mobile-manage-account', {
                redirect: 'root',
                cause: 'mobile user',
            });
            setOpenMobileModal(true);
            return;
        }
        await openStripe();
    };

    const onUpgradeClick = () => {
        if (user?.paymentProvider === PaymentProvider.AppStore) {
            mixpanel?.track('goto-mobile-manage-account', {
                redirect: 'root',
                cause: 'mobile user',
            });
            setOpenMobileModal(true);
            return;
        }
        mixpanel?.track(cancelAt ? 'renew-plan' : 'upgrade', {
            from: 'account-settings',
            event: 'click',
        });
        window.open(pricingPageUrl, '_blank');
    };

    const showUnlimitedCredits = unlimitedFirstMonthCreditsEnabled && (credits?.remaining ?? 0) > 0;

    const renderCredits = () => {
        if (isAnonymous || (cancelAt && cancelAt * 1000 < Date.now())) {
            return null;
        }
        if (!isCreditsFetched || !unlimitedFirstMonthCreditsIsFetched) {
            return (
                <>
                    <CreditsSkeleton width="100%" />
                </>
            );
        }

        return (
            <>
                <CreditsContainer>
                    <img src={CrownBlack} />
                    <Grid justifyContent="space-between">
                        {creditsToMinutesEnabled ? (
                            <>
                                <Typography variant="h4" fontWeight={600}>
                                    Minutes left: {'\r'}
                                    <UsedCredits isExceeding={credits!.remaining < 0}>
                                        {' '}
                                        {creditsToVideoMinutes(credits!.remaining)}
                                    </UsedCredits>
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="h4" fontWeight={600}>
                                    Credits left: {'\r'}
                                    <UsedCredits isExceeding={credits!.remaining < 0}>
                                        {showUnlimitedCredits ? 'Unlimited' : formatCredits(credits!.remaining)}
                                    </UsedCredits>
                                </Typography>
                            </>
                        )}
                    </Grid>
                </CreditsContainer>
            </>
        );
    };

    const renderBillingDate = () => {
        if (isAnonymous || (!invoices && !isFetchingInvoices) || cancelAt) {
            return null;
        }

        if (!invoices) {
            return <CreditsSkeleton width="100%" />;
        }

        const seconds = invoices.invoice?.next_payment_attempt;
        const date = seconds ? format(new Date(seconds * 1000), 'dd/MM/yyyy') : '';

        return <BillingCycleTypography>{`Your next billing cycle begins on ${date}.`}</BillingCycleTypography>;
    };

    const renderCancelMessage = () => {
        return (
            cancelAt && (
                <CancelMessageContainer>
                    <div>
                        {`Your subscription ${cancelAt * 1000 > Date.now() ? 'will end' : 'ended'} on `}
                        <CancelDate>{format(new Date(cancelAt * 1000), 'dd/MM/yyyy')}</CancelDate>
                    </div>
                    <div>
                        If there's something we can do to improve please let us know at
                        <SupportLink to={supportUrl} target="_blank">
                            {' support@d-id.com'}
                        </SupportLink>
                    </div>
                </CancelMessageContainer>
            )
        );
    };

    const HandleCancelSubscription = async () => {
        if (user?.paymentProvider === PaymentProvider.AppStore) {
            mixpanel?.track('goto-mobile-manage-account', {
                redirect: 'root',
                cause: 'mobile user',
            });
            setOpenMobileModal(true);
            return;
        }
        setIsCancelingSubscription(true);
        if (fetchingCustomerPortalUrl) return;
        mixpanel?.track('cancel-subscription', {
            event: 'click',
            from: 'settings',
            product_id: user?.productId,
            price_id: user?.priceId,
            plan_group: user?.planGroup,
        });

        if ((cancelationFlag as cancelationProcessFlag) === cancelationProcessFlag.churnkey) {
            const userChurnkeyHash = await getChurnKey();
            logger.debug('open churnkey on mode:', { churnkeyEnvironment });
            window.churnkey.init('show', {
                subscriptionId: user?.stripeSubscriptionId || '',
                customerId: user?.stripeCustomerId || '',
                authHash: userChurnkeyHash,
                appId: churnkeyAppId,
                mode: churnkeyEnvironment,
                provider: 'stripe',
                record: false,
                handleSupportRequest: () => {
                    window.open('https://share-eu1.hsforms.com/17NVrWCsgSUK-eEwcw59QKgf6589', '_blank');
                    window.churnkey.hide();
                },
            });
        } else {
            setIsCancelationDialogOpen(true);
        }
        queryClient.invalidateQueries({ queryKey: ['get-last-subscription', 'get-next-invoice'] });
        setIsCancelingSubscription(false);
    };

    async function openStripe() {
        const { data } = await refetchCustomerPortalUrl();
        if (data?.status === HttpStatus.OK) {
            window.open(data?.url);
        } else {
            toast.error({ title: 'Could not find user billing data' });
        }
    }

    return (
        <>
            <Card flex={flex} gap={0} hide={Boolean(isAnonymous)} bg="white">
                <BillingTitle variant="h3">Plan & Billing</BillingTitle>
                <FieldsContainer>
                    <PlanBadge variant="h6" canceled={!!cancelAt}>
                        {getPlanText({
                            isOrg,
                            isOrgNameFetched,
                            orgName: org?.name ?? '',
                            user,
                            cancelAt,
                            credits,
                            creditsToMinutesEnabled,
                            unlimitedFirstMonthCreditsEnabled,
                        })}
                    </PlanBadge>
                    {renderCredits()}
                    {renderBillingDate()}
                    {renderCancelMessage()}
                </FieldsContainer>
                <ActionButtonsContainer>
                    <ActionButton color="primary" onClick={onUpgradeClick} disabled={false}>
                        {cancelAt ? 'Renew plan' : 'Change plan'}
                    </ActionButton>
                    <SeparateLine />
                    <ActionButton
                        color="secondary"
                        variant="outlined"
                        onClick={onBillingClick}
                        disabled={
                            [PlanGroup.GUEST, PlanGroup.TRIAL, PlanGroup.ENTERPRISE, PlanGroup.ENTERPRISE_TRIAL, PlanGroup.FREE].includes(
                                user?.planGroup as any
                            ) || fetchingCustomerPortalUrl
                        }>
                        View billing
                    </ActionButton>
                    <ActionButton
                        color="secondary"
                        variant="outlined"
                        onClick={HandleCancelSubscription}
                        disabled={!!isCancelingSubscription || !user?.stripeSubscriptionId}
                        hidden={!!cancelAt}>
                        Cancel plan
                    </ActionButton>
                </ActionButtonsContainer>
                <CancelModal
                    isOpen={isCancelationDialogOpen}
                    setIsOpen={setIsCancelationDialogOpen}
                    setOpenMobileModal={setOpenMobileModal}
                    cancelationFlag={cancelationFlag}
                    isSuggestDiscount={isSuggestDiscount}
                    credits={credits}
                />
            </Card>
            <SimpleNotificationModal
                open={openMobileModal}
                onClose={() => {
                    setOpenMobileModal(false);
                }}
                content="Please use the mobile app to manage your plan."
                primaryActionContent="Got It"
                onPrimaryAction={() => {
                    setOpenMobileModal(false);
                }}
                buttonPlacement="center"
            />
        </>
    );
}
